import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import AdminButton from 'components/common/AdminButton';

/**
 * Employee form validation schema
 */
const getValidationSchema = (clientId, employeeId = null) => Yup.object().shape({
  firstName: Yup.string()
    .required('First name is required')
    .max(100, 'First name must be less than 100 characters')
    .trim(),
  
  lastName: Yup.string()
    .required('Last name is required')
    .max(100, 'Last name must be less than 100 characters')
    .trim(),
  
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format')
    .max(255, 'Email must be less than 255 characters'),
  
  phoneNumber: Yup.string()
    .matches(/^[\d\s\-\(\)\+]*$/, 'Invalid phone number format')
    .min(10, 'Phone number must be at least 10 digits'),
  
  employmentType: Yup.string()
    .oneOf(['FULL_TIME', 'PART_TIME', 'CONTRACTOR', 'TEMPORARY', 'SEASONAL'], 'Invalid employment type'),
  
  hireDate: Yup.date()
    .max(new Date(), 'Hire date cannot be in the future')
    .nullable(),
  
  terminationDate: Yup.date()
    .min(Yup.ref('hireDate'), 'Termination date must be after hire date')
    .nullable(),
  
  // RadixDash access fields (conditional)
  grantSystemAccess: Yup.boolean(),
  
  userName: Yup.string().when('grantSystemAccess', {
    is: true,
    then: Yup.string()
      .required('Username is required for RadixDash access')
      .min(3, 'Username must be at least 3 characters')
      .max(50, 'Username must be less than 50 characters'),
    otherwise: Yup.string()
  }),
  
  userRole: Yup.string().when('grantSystemAccess', {
    is: true,
    then: Yup.string()
      .required('User role is required for RadixDash access')
      .oneOf(['Admin', 'Manager', 'User'], 'Invalid user role'),
    otherwise: Yup.string()
  })
});

/**
 * Form component for creating and editing employee records
 * @param {Object} props - Component props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Handler to close dialog
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {Object} props.employee - Employee record for editing (null for create)
 * @param {string} props.clientId - Client identifier
 * @param {boolean} props.loading - Loading state during submission
 * @returns {React.Component} Employee form dialog
 */
function EmployeeForm({ 
  open, 
  onClose, 
  onSubmit, 
  employee = null, 
  clientId,
  loading = false 
}) {
  const isEdit = !!employee;
  const [showSystemAccess, setShowSystemAccess] = useState(false);

  const initialValues = {
    firstName: employee?.firstName || '',
    lastName: employee?.lastName || '',
    email: employee?.email || '',
    phoneNumber: employee?.phoneNumber || '',
    employmentType: employee?.employmentType || '',
    hireDate: employee?.hireDate ? new Date(employee.hireDate) : null,
    terminationDate: employee?.terminationDate ? new Date(employee.terminationDate) : null,
    tags: employee?.tags || [],
    grantSystemAccess: false,
    userName: '',
    userRole: '',
    isEdit: isEdit
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const submitData = { ...values };

      // Remove isEdit flag (not needed in submission)
      delete submitData.isEdit;

      // Remove RadixDash access fields if not granting access
      if (!submitData.grantSystemAccess) {
        delete submitData.userName;
        delete submitData.userRole;
        delete submitData.grantSystemAccess;
      }
      // Keep grantSystemAccess, userName, userRole if checkbox is checked

      // Format dates
      if (submitData.hireDate) {
        submitData.hireDate = new Date(submitData.hireDate).toISOString().split('T')[0];
      }
      if (submitData.terminationDate) {
        submitData.terminationDate = new Date(submitData.terminationDate).toISOString().split('T')[0];
      }

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      setFieldError('general', error.message || 'Failed to save employee');
      setSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isEdit ? 'Edit Employee' : 'Add New Employee'}
      </DialogTitle>
      
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(clientId, employee?.id)}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
        validateOnMount={true}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, isValid, dirty }) => {
          // Check if required fields have values
          const hasRequiredFields = values.firstName && values.lastName && values.email;
          const hasSystemAccessFields = !values.grantSystemAccess ||
            (values.userName && values.userRole);
          const isFormValid = hasRequiredFields && hasSystemAccessFields && isValid;
          
          return (
          <Form>
            <DialogContent dividers>
              {errors.general && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.general}
                </Alert>
              )}

              {/* Basic Information */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Basic Information
              </Typography>
              
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3}>
                <Field
                  as={TextField}
                  name="firstName"
                  label="First Name"
                  fullWidth
                  required
                  error={touched.firstName && !!errors.firstName}
                  helperText={touched.firstName && errors.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                />
                
                <Field
                  as={TextField}
                  name="lastName"
                  label="Last Name"
                  fullWidth
                  required
                  error={touched.lastName && !!errors.lastName}
                  helperText={touched.lastName && errors.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                />
                
                <Field
                  as={TextField}
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                />
                
                <Field
                  as={TextField}
                  name="phoneNumber"
                  label="Phone Number"
                  fullWidth
                  error={touched.phoneNumber && !!errors.phoneNumber}
                  helperText={touched.phoneNumber && errors.phoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                />
              </Box>

              {/* Employment Details */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Employment Details
              </Typography>
              
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3}>
                <FormControl fullWidth error={touched.employmentType && !!errors.employmentType}>
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    name="employmentType"
                    value={values.employmentType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Employment Type"
                    disabled={loading}
                    sx={{ height: 40 }}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="FULL_TIME">Full Time</MenuItem>
                    <MenuItem value="PART_TIME">Part Time</MenuItem>
                    <MenuItem value="CONTRACTOR">Contractor</MenuItem>
                    <MenuItem value="TEMPORARY">Temporary</MenuItem>
                    <MenuItem value="SEASONAL">Seasonal</MenuItem>
                  </Select>
                </FormControl>
                
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Hire Date"
                    value={values.hireDate}
                    onChange={(date) => setFieldValue('hireDate', date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={touched.hireDate && !!errors.hireDate}
                        helperText={touched.hireDate && errors.hireDate}
                        disabled={loading}
                      />
                    )}
                    disabled={loading}
                  />
                </LocalizationProvider>
              </Box>

              {/* RadixDash Access (only for new employees) */}
              {!isEdit && (
                <>
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    RadixDash Access (Optional)
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.grantSystemAccess}
                        onChange={(e) => {
                          setFieldValue('grantSystemAccess', e.target.checked);
                          setShowSystemAccess(e.target.checked);
                        }}
                        disabled={loading}
                      />
                    }
                    label="Grant RadixDash access to this employee"
                  />
                  
                  {showSystemAccess && (
                    <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mt={2}>
                      <Field
                        as={TextField}
                        name="userName"
                        label="Username"
                        fullWidth
                        required={values.grantSystemAccess}
                        error={touched.userName && !!errors.userName}
                        helperText={touched.userName && errors.userName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={loading}
                      />

                      <FormControl
                        fullWidth
                        required={values.grantSystemAccess}
                        error={touched.userRole && !!errors.userRole}
                      >
                        <InputLabel>User Role</InputLabel>
                        <Select
                          name="userRole"
                          value={values.userRole}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="User Role"
                          disabled={loading}
                          sx={{ height: 40 }}
                        >
                          <MenuItem value="">Select Role</MenuItem>
                          <MenuItem value="Admin">Admin</MenuItem>
                          <MenuItem value="Manager">Manager</MenuItem>
                          <MenuItem value="User">User</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {showSystemAccess && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      A temporary password will be automatically generated and emailed to the employee's email address.
                    </Alert>
                  )}
                </>
              )}
            </DialogContent>
            
            <DialogActions>
              <AdminButton
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </AdminButton>
              <AdminButton
                type="submit"
                variant="contained"
                disabled={loading || isSubmitting || !isFormValid}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {isEdit ? 'Save Changes' : 'Add Employee'}
              </AdminButton>
            </DialogActions>
          </Form>
          )}}
      </Formik>
    </Dialog>
  );
}

EmployeeForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  employee: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    employmentType: PropTypes.string,
    hireDate: PropTypes.string,
    terminationDate: PropTypes.string,
    tags: PropTypes.array
  }),
  clientId: PropTypes.string.isRequired,
  loading: PropTypes.bool
};

export default EmployeeForm;